import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Anchor, Fuel, Calendar, Clock, TrendingUp, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { BACKEND_URL, API_URL } from '../config/api';

const API = API_URL;

const FUEL_LEVELS = [
  { value: 'full', label: 'Full', color: 'bg-green-500' },
  { value: 'three-quarter', label: '3/4 Full', color: 'bg-lime-500' },
  { value: 'half', label: 'Half', color: 'bg-yellow-500' },
  { value: 'quarter', label: '1/4 Full', color: 'bg-orange-500' },
  { value: 'empty', label: 'Empty', color: 'bg-red-500' }
];

const TeamMemberHistory = ({ teamMemberId, teamMemberData }) => {
  const [pastBookings, setPastBookings] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFuelDialog, setShowFuelDialog] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [fuelReport, setFuelReport] = useState({
    oldFuelLevel: '',
    newFuelLevel: ''
  });
  const [stats, setStats] = useState({
    totalBookings: 0,
    totalHours: 0,
    assetsUsed: new Set()
  });

  useEffect(() => {
    fetchData();
  }, [teamMemberId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tripsRes, assetsRes] = await Promise.all([
        axios.get(`${API}/trips`),
        axios.get(`${API}/assets`)
      ]);

      const allBookings = tripsRes.data;
      const today = new Date().toISOString().split('T')[0];

      // Filter teamMember's past trips
      const past = allBookings
        .filter(t => t.teamMemberId === teamMemberId && t.date < today)
        .sort((a, b) => {
          if (b.date === a.date) return b.startTime.localeCompare(a.startTime);
          return b.date.localeCompare(a.date);
        });

      setPastBookings(past);
      setAssets(assetsRes.data);

      // Calculate stats
      const totalHours = past.reduce((sum, t) => sum + (t.duration || 0), 0) / 60;
      const assetsUsed = new Set(past.filter(t => t.assetId).map(t => t.assetId));

      setStats({
        totalBookings: past.length,
        totalHours: totalHours.toFixed(1),
        assetsUsed
      });
    } catch (error) {
      console.error("Error fetching history:", error);
      toast.error("Failed to load trip history");
    } finally {
      setLoading(false);
    }
  };

  const getAssetDetails = (assetId) => {
    return assets.find(b => b.id === assetId);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const handleOpenFuelReport = (trip) => {
    const asset = getAssetDetails(trip.assetId);
    
    // Check if another teamMember used this asset after this trip
    const laterBookings = pastBookings.filter(t => 
      t.assetId === trip.assetId && 
      (t.date > trip.date || (t.date === trip.date && t.startTime > trip.startTime))
    );
    
    const canEdit = laterBookings.length === 0;
    
    if (!canEdit) {
      toast.error("Cannot edit fuel - another teamMember used this asset after your trip");
      return;
    }
    
    setSelectedBooking(trip);
    setFuelReport({
      oldFuelLevel: asset?.fuelLevel || 'full',
      newFuelLevel: ''
    });
    setShowFuelDialog(true);
  };

  const handleSubmitFuelReport = async () => {
    if (!fuelReport.newFuelLevel) {
      toast.error("Please select the current fuel level");
      return;
    }

    try {
      // Directly update the asset's fuel level
      await axios.patch(
        `${API}/assets/${selectedBooking.assetId}/fuel?fuelLevel=${fuelReport.newFuelLevel}`
      );

      toast.success("Fuel level updated successfully!");
      setShowFuelDialog(false);
      setSelectedBooking(null);
      
      // Refresh data to show new fuel level
      fetchData();
    } catch (error) {
      console.error("Error updating fuel level:", error);
      toast.error("Failed to update fuel level");
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading history...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Booking History</h2>
        <p className="text-gray-600 mt-1">View your completed trips and statistics</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Bookings</p>
                <p className="text-3xl font-bold text-blue-600">{stats.totalBookings}</p>
                <p className="text-xs text-gray-500 mt-1">completed</p>
              </div>
              <CheckCircle2 className="w-10 h-10 text-blue-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Hours</p>
                <p className="text-3xl font-bold text-green-600">{stats.totalHours}</p>
                <p className="text-xs text-gray-500 mt-1">on the water</p>
              </div>
              <Clock className="w-10 h-10 text-green-500 opacity-20" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Assets Used</p>
                <p className="text-3xl font-bold text-purple-600">{stats.assetsUsed.size}</p>
                <p className="text-xs text-gray-500 mt-1">different assets</p>
              </div>
              <Anchor className="w-10 h-10 text-purple-500 opacity-20" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Past Bookings List */}
      <Card>
        <CardHeader>
          <CardTitle>Past Bookings</CardTitle>
        </CardHeader>
        <CardContent>
          {pastBookings.length > 0 ? (
            <div className="space-y-3">
              {pastBookings.map((trip) => {
                const asset = getAssetDetails(trip.assetId);
                
                // Check if teamMember can edit fuel for this trip
                const laterBookings = pastBookings.filter(t => 
                  t.assetId === trip.assetId && 
                  (t.date > trip.date || (t.date === trip.date && t.startTime > trip.startTime))
                );
                const canEditFuel = laterBookings.length === 0;
                
                return (
                  <div
                    key={trip.id}
                    className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <p className="font-semibold text-gray-800">
                            {formatDate(trip.date)}
                          </p>
                          <Clock className="w-4 h-4 text-gray-500" />
                          <p className="text-gray-600">{formatTime(trip.startTime)}</p>
                          <Badge variant="outline" className="text-xs">
                            {trip.duration}min
                          </Badge>
                        </div>
                        
                        {asset && (
                          <div className="flex items-center gap-2 mb-2">
                            <Anchor className="w-4 h-4 text-blue-600" />
                            <p className="text-sm text-gray-700 font-medium">{asset.name}</p>
                            <Badge variant="secondary" className="text-xs">
                              {asset.type}
                            </Badge>
                            {asset.fuelLevel && (
                              <div className="flex items-center gap-1 text-xs text-gray-600">
                                <Fuel className="w-3 h-3" />
                                <span className="capitalize">{asset.fuelLevel}</span>
                              </div>
                            )}
                          </div>
                        )}

                        {trip.notes && (
                          <p className="text-sm text-gray-600 mt-2 bg-white p-2 rounded border">
                            {trip.notes}
                          </p>
                        )}
                        
                        {!canEditFuel && asset && (
                          <p className="text-xs text-orange-600 mt-2 italic">
                            ⚠️ Fuel locked - asset used by another teamMember after this trip
                          </p>
                        )}
                      </div>

                      <div className="ml-4">
                        {asset && canEditFuel && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenFuelReport(trip)}
                          >
                            <Fuel className="w-4 h-4 mr-2" />
                            Report Fuel
                          </Button>
                        )}
                        {asset && !canEditFuel && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled
                            className="opacity-50 cursor-not-allowed"
                          >
                            <Fuel className="w-4 h-4 mr-2" />
                            Fuel Locked
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12">
              <CheckCircle2 className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600 font-medium">No completed trips yet</p>
              <p className="text-sm text-gray-500 mt-1">Your trip history will appear here after completing assignments</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fuel Report Dialog */}
      <Dialog open={showFuelDialog} onOpenChange={setShowFuelDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report Fuel Level</DialogTitle>
          </DialogHeader>
          {selectedBooking && (
            <div className="space-y-4 mt-4">
              <div className="bg-blue-50 p-3 rounded border border-blue-200">
                <p className="text-sm font-medium text-blue-900">
                  {getAssetDetails(selectedBooking.assetId)?.name}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Booking: {formatDate(selectedBooking.date)} at {formatTime(selectedBooking.startTime)}
                </p>
              </div>

              <div>
                <Label>Fuel Level Before Booking</Label>
                <Select value={fuelReport.oldFuelLevel} disabled>
                  <SelectTrigger className="bg-gray-50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FUEL_LEVELS.map(level => (
                      <SelectItem key={level.value} value={level.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${level.color}`} />
                          {level.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Current Fuel Level *</Label>
                <Select 
                  value={fuelReport.newFuelLevel} 
                  onValueChange={(value) => setFuelReport({ ...fuelReport, newFuelLevel: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select current fuel level" />
                  </SelectTrigger>
                  <SelectContent>
                    {FUEL_LEVELS.map(level => (
                      <SelectItem key={level.value} value={level.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full ${level.color}`} />
                          {level.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setShowFuelDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleSubmitFuelReport}>
                  Submit Report
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamMemberHistory;
